// Auth token store, held at module scope (mirrors config/system.js): both the
// axios interceptors (which can't read React state) and AuthContext need the
// same tokens. Persisted in localStorage so a session survives reloads.
//
// Shapes follow the API (server/routes/auth.js): auth success payloads carry
// { access_token, refresh_token, user, ... } — setTokens accepts that payload
// directly.

const ACCESS_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null; // localStorage unavailable (private mode / SSR)
  }
};

let accessToken = read(ACCESS_KEY);
let refreshToken = read(REFRESH_KEY);

export const getAccessToken = () => accessToken;

/**
 * The session as the stored access token states it, before the server has
 * confirmed it (issue 644): the person's id, email and handle are CLAIMS on
 * the token (server/utils/authTokens.js), so a page that boots with a token
 * can render its signed-in header and decide whether to show the handle
 * prompt at first paint, instead of booting anonymous and shifting everything
 * when the session read answers. Nothing here verifies the token — the server
 * does that on the next request — so the shape is `provisional: true` and
 * carries only what the claims say; /api/auth/me replaces it whole. A token
 * that does not decode yields null, which is the anonymous boot as before.
 */
export const provisionalUser = () => {
  if (!accessToken) return null;
  try {
    const part = accessToken.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json);
    if (!claims || typeof claims.sub !== 'string' || typeof claims.email !== 'string') return null;
    return {
      id: claims.sub,
      email: claims.email,
      handle: typeof claims.handle === 'string' ? claims.handle : null,
      provisional: true,
    };
  } catch (e) {
    return null;
  }
};
export const getRefreshToken = () => refreshToken;

export const setTokens = ({ access_token: access, refresh_token: refresh } = {}) => {
  accessToken = access || null;
  refreshToken = refresh || null;
  try {
    if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
    else localStorage.removeItem(ACCESS_KEY);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    else localStorage.removeItem(REFRESH_KEY);
  } catch (e) {
    /* localStorage unavailable — keep the in-memory values */
  }
};

export const clearTokens = () => setTokens({});

// Forced-logout broadcast: the axios layer clears tokens when a refresh fails
// (session truly dead) and AuthContext subscribes to drop the user from React
// state. A tiny listener set instead of window events keeps it testable.
const logoutListeners = new Set();

export const onAuthLogout = (fn) => {
  logoutListeners.add(fn);
  return () => logoutListeners.delete(fn);
};

export const emitAuthLogout = () => {
  logoutListeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      /* one bad listener must not break the rest */
    }
  });
};
